import { Trans } from '@lingui/react/macro';
import { ArrowRight } from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { Button, CardContent } from 'src/components';
import { ExpirationText } from 'src/components/Offers/ExpirationText';
import { PromotionCheckoutLink } from 'src/components/PromotionCheckoutLink';
import { useFreeTrialDuration } from 'src/utils/freeTrialLength';
import { UnreachableCodeError } from 'src/utils/unreachableCode';
import { URLS } from 'src/utils/urls/urls';

export const ConnectOfferInfo = ({
  offerType,
  expiresAt,
  freeTrialLengthDays,
}: {
  offerType: 'connectOffer' | 'dmOffer' | 'winbackOffer';
  expiresAt: string | null;
  freeTrialLengthDays: number;
}) => {
  const trialDuration = useFreeTrialDuration(freeTrialLengthDays);
  switch (offerType) {
    case 'connectOffer':
      return (
        <div data-cy="subscription-offer-info">
          <div className="mb-16">
            <ExpirationText expirationDate={expiresAt} />
          </div>

          <Typography variant="heading-subtle-2xl" as="h2">
            <Trans>Activate your {trialDuration} free Connect trial</Trans>
          </Typography>
          <CardContent dataCy="subscription-no-plan" className="pb-24">
            <Typography variant="body-md">
              <Trans>
                Get unlimited cloud storage, seamless integrations, handwriting
                search, and a growing list of tools built to suit your workflow.
              </Trans>
            </Typography>
          </CardContent>
          <div className="flex flex-wrap items-center gap-24">
            <Button
              variant="primary"
              as="a"
              to={URLS.STORE_CONNECT_OFFER_LANDING}
            >
              <span>
                <Trans>Activate Connect</Trans>
              </span>
              <ArrowRight size={24} />
            </Button>
            <PromotionCheckoutLink
              componentLocation={ComponentLocations.SUBSCRIPTION.PLAN_PAGE}
            />
          </div>
        </div>
      );
    case 'dmOffer':
      return (
        <div data-cy="subscription-offer-info">
          <Typography variant="heading-subtle-2xl" as="h2">
            <Trans>Activate your {trialDuration} free Connect trial</Trans>
          </Typography>
          <CardContent dataCy="subscription-no-plan" className="pb-24">
            <Typography variant="body-md">
              <Trans>
                Get unlimited cloud storage, seamless integrations, handwriting
                search, and a growing list of tools built to suit your workflow.
              </Trans>
            </Typography>
          </CardContent>
          <div className="flex flex-wrap items-center gap-24">
            <Button variant="primary" as="a" to={URLS.STORE_DM_OFFER_LANDING}>
              <span>
                <Trans>Activate Connect</Trans>
              </span>
              <ArrowRight size={24} />
            </Button>
            <PromotionCheckoutLink
              componentLocation={ComponentLocations.SUBSCRIPTION.PLAN_PAGE}
            />
          </div>
        </div>
      );
    case 'winbackOffer':
      return (
        <div className="flex flex-col gap-24" data-cy="subscription-offer-info">
          <Tag size="large" variant="info-light">
            <Trans>Limited Time Offer</Trans>
          </Tag>

          <Typography variant="heading-subtle-2xl" as="h2">
            <Trans>Claim your {trialDuration} free trial</Trans>
          </Typography>
          <CardContent className="pb-0" dataCy="subscription-no-plan" borderTop>
            <Typography variant="body-md">
              <Trans>
                Get exclusive templates from reMarkable Methods today, and work
                conveniently across your devices with our apps. Get even more
                out of your workday with Connect.
              </Trans>
            </Typography>
          </CardContent>
          <div className="flex flex-wrap items-center gap-24">
            <Button
              variant="primary"
              as="a"
              to={URLS.STORE_METHODS_OFFER_CHECKOUT}
              analytics={{
                location: ComponentLocations.SUBSCRIPTION.PLAN_PAGE,
                text: 'Try Connect for free',
                action: 'activate winback offer',
              }}
            >
              <span>
                <Trans>Try Connect for free</Trans>
              </span>
              <ArrowRight size={24} />
            </Button>
            <PromotionCheckoutLink
              componentLocation={ComponentLocations.SUBSCRIPTION.PLAN_PAGE}
            />
          </div>
        </div>
      );
    default:
      throw new UnreachableCodeError(offerType);
  }
};
